import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { toast } from 'react-toastify';
import Link from '~/components/Link';
import Form from '~/components/Form';
import * as S from '~/components/Form/styles';
import Input from '~/components/Form/Input';
import routesPath from '~/constants/routes';
import FormConfig from '~/pages/Login/components/FormConfig';
import Captcha from '~/components/Form/Captcha';
import DefaultLayout from '~/pages/_layouts/DefaultLayout';
import { login, confirmRegistration, checkAzureLogin } from '~/services/api';
import { languageOptions } from '~/utils/languageOptions';
import { FoldedMenu } from '~/components';
import { signInSuccess } from '~/store/modules/auth/actions';
import history from '~/config/history';

export default function Login() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [captchaValue, setCaptchaValue] = useState(null);
  const [captchaError, setCaptchaError] = useState('');
  const [accountConfirmationMessage, setAccountConfirmationMessage] = useState(
    false
  );
  const [viewLoginInputs, setViewLoginInputs] = useState(true);
  const [loginCheckLoading, setLoginCheckLoading] = useState(false);
  const [phoneDigits, setPhoneDigits] = useState('');
  const { loading } = useSelector(state => state.auth);

  const handleReCaptchaVerify = useCallback(async () => {
    if (executeRecaptcha) {
      const token = await executeRecaptcha('login');
      setCaptchaValue(token);

      return token;
    }

    return '';
  }, [executeRecaptcha]);

  useEffect(() => {
    handleReCaptchaVerify();
  }, [handleReCaptchaVerify]);

  const checkValidations = () => {
    const token = handleReCaptchaVerify();

    if (token !== null && token !== '') {
      setCaptchaError('');
    } else {
      setCaptchaError(t('captchaIsRequiredValidationError'));
    }
  };

  const promptRegistrationConfirmation = useCallback(
    async code => {
      try {
        await confirmRegistration(code);
        setAccountConfirmationMessage(true);
      } catch ({ response }) {
        if (response.status === 401) toast.error(t('userActivated'));
        setAccountConfirmationMessage(false);
      }
    },
    [setAccountConfirmationMessage, t]
  );

  const checkIfLoggedWithAzure = useCallback(() => {
    const fetchTokens = async () => {
      try {
        const { data } = await checkAzureLogin();
        dispatch(signInSuccess(data.token, data.refreshToken));
      } catch ({ response }) {
        if (response.status === 401) {
          history.push(`/failure`);
        }
      }
    };
    fetchTokens();
  }, [dispatch]);

  useEffect(() => {
    const code = localStorage.getItem('confirmationCode');
    if (code) {
      promptRegistrationConfirmation(code);
    }
    localStorage.removeItem('confirmationCode');
  }, [promptRegistrationConfirmation]);

  useEffect(() => {
    const loginOffice365 = localStorage.getItem('loginOffice365');
    if (loginOffice365) {
      checkIfLoggedWithAzure();
      localStorage.removeItem('loginOffice365');
    }
  }, [checkIfLoggedWithAzure]);

  const handleGoToMFA = async (values, errors) => {
    setLoginCheckLoading(true);
    const token = await handleReCaptchaVerify();
    if (token !== null && token !== '') {
      if (Object.keys(errors).length === 1) {
        try {
          const {
            data: { phone_digits },
          } = await login({
            email: values.email,
            password: values.password,
            'g-recaptcha-response': token,
          });
          setPhoneDigits(phone_digits);
          setViewLoginInputs(false);
        } catch (err) {
          const {
            response: {
              data: { error },
            },
          } = err;
          handleReCaptchaVerify();
          setViewLoginInputs(true);
          toast.error(error);
        }
      }
    } else {
      setCaptchaError(t('captchaIsRequiredValidationError'));
    }
    setLoginCheckLoading(false);
  };

  return (
    <DefaultLayout>
      <FormConfig captchaValue={captchaValue} setCaptchaError={setCaptchaError}>
        {props => {
          // eslint-disable-next-line react/prop-types
          const { isSubmitting, handleSubmit, errors, values } = props;
          return (
            <S.FormContainer width={30} height="auto">
              <Form
                hasWidth={100}
                onSubmit={handleSubmit}
                position={!viewLoginInputs && 'flex-start'}
                hasHeight="auto"
                flex
              >
                <S.Logo />
                <S.FormTitle>
                  {viewLoginInputs ? t('loginIntoAccount') : t('enterSmsCode')}
                </S.FormTitle>
                {!viewLoginInputs && (
                  <S.FormSubtitle>{`${t(
                    'smsCodeNotification_part_1'
                  )}${phoneDigits}${t(
                    'smsCodeNotification_part_2'
                  )}`}</S.FormSubtitle>
                )}
                {accountConfirmationMessage && (
                  <S.ConfirmationSuccessMessage>
                    {t('confirmationSuccessMessage')}
                  </S.ConfirmationSuccessMessage>
                )}
                {viewLoginInputs ? (
                  <>
                    <Input
                      icon="mail"
                      type="email"
                      selector="email"
                      label={t('email')}
                      placeholder={t('email')}
                      formikProps={props}
                      disabled={loginCheckLoading}
                    />

                    <Input
                      icon="lock"
                      type="password"
                      selector="password"
                      label={t('password')}
                      placeholder={t('password')}
                      formikProps={props}
                      disabled={loginCheckLoading}
                    />

                    <Captcha onVerify={setCaptchaValue} />
                  </>
                ) : (
                  <Input
                    icon="lock"
                    type="text"
                    selector="code"
                    label={t('smsCode')}
                    formikProps={props}
                    disabled={loading}
                  />
                )}

                {viewLoginInputs ? (
                  <S.SubmitButton
                    type="button"
                    disabled={loginCheckLoading}
                    onClick={() => {
                      handleGoToMFA(values, errors);
                    }}
                  >
                    {!loginCheckLoading
                      ? t('loginIntoAccount')
                      : `${t('loading')}...`}
                  </S.SubmitButton>
                ) : (
                  <S.SubmitButton
                    type="submit"
                    disabled={isSubmitting || loading}
                    onClick={() => {
                      checkValidations();
                    }}
                  >
                    {isSubmitting || loading
                      ? `${t('loading')}...`
                      : t('confirm')}
                  </S.SubmitButton>
                )}

                <Captcha
                  onVerify={setCaptchaValue}
                  captchaError={captchaError}
                />
                {viewLoginInputs && (
                  <S.Return>
                    <Link to={routesPath.passwordRecovery}>
                      {t('passwordRecovery')}
                    </Link>
                  </S.Return>
                )}
              </Form>
              {viewLoginInputs && (
                <S.RegisterZone>
                  <p>{t('noHaveAccount')}</p>
                  <div>
                    <Link to={routesPath.register}>{t('register')}</Link>
                  </div>
                </S.RegisterZone>
              )}
              <S.RegisterZone>
                <div>
                  <button
                    type="button"
                    onClick={() => {
                      localStorage.setItem('loginOffice365', true);
                      window.location = process.env.REACT_APP_AZURE_LOGIN_URL;
                    }}
                  >
                    {t('privateAccess')}
                  </button>
                </div>
              </S.RegisterZone>
            </S.FormContainer>
          );
        }}
      </FormConfig>
      <FoldedMenu label={t('languageId')} options={languageOptions()} />
    </DefaultLayout>
  );
}
